import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Button, Form, Input, Select, Switch, Radio, Row, Col,
  InputNumber, Upload, Tooltip, Typography, Divider, Modal, Tag, Space, Drawer, message
} from 'antd';
import { InfoCircleOutlined, UploadOutlined, ArrowLeftOutlined, GiftOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const PRODUCTS = [
  { value: 'PROD_Q1', label: '智能摄像机Q1' },
  { value: 'PROD_P1', label: '云台机P1' },
  { value: 'PROD_F1', label: '烟雾报警器F1' },
  { value: 'PROD_S1', label: '智能门锁S1' },
  { value: 'PROD_CAM2', label: '户外摄像机C2' },
];

// 方案产品（PCBA）
const PLAN_PRODUCTS = [
  { value: 'P_ECO_001', label: '智能云台摄像机方案' },
  { value: 'P_ECO_002', label: '4G低功耗电池摄像机方案' },
  { value: 'P_ECO_003', label: '智能门铃方案' },
];

const FIRMWARE_OPTIONS = [
  { value: 'fw_main_v1.0', label: '主控固件 / V1.0.0', tested: true, finalized: true },
  { value: 'fw_main_v1.1', label: '主控固件 / V1.1.0', tested: true, finalized: false },
  { value: 'fw_main_v2.0', label: '主控固件 / V2.0.0', tested: false, finalized: false },
  { value: 'fw_wifi_v1.0', label: 'WiFi模组固件 / V1.0.0', tested: true, finalized: true },
  { value: 'fw_wifi_v1.2', label: 'WiFi模组固件 / V1.2.0', tested: false, finalized: false },
  { value: 'fw_mcu_v1.0', label: 'MCU固件 / V1.0.0', tested: true, finalized: true },
  { value: 'fw_mcu_v1.1', label: 'MCU固件 / V1.1.0', tested: true, finalized: true },
];

// 固件关联的配置和生产测试项（mock）
const MOCK_FW_CONFIG = {
  'fw_main_v1.0': {
    testItems: ['图像测试', 'WIFI信号', '电池测试', 'TF卡读写', '指示灯测试', '版本号测试'],
    otaTest: true, otaVersion: 'V1.1.0', otaFileName: 'main_fw_v1.1.0_ota.bin',
    config: [
      { label: '硬件类型', value: '摇头机' },
      { label: '光源类型', value: '红外' },
      { label: '分辨率', value: '1080P' },
      { label: '云台上下方向', value: '正向' },
      { label: '云台左右方向', value: '正向' },
      { label: '语言', value: '中文' },
    ],
  },
  'fw_main_v1.1': {
    testItems: ['图像测试', 'WIFI信号', '电池测试', 'PTZ测试', '麦克风测试', '喇叭测试', '版本号测试'],
    config: [
      { label: '硬件类型', value: '摇头机' },
      { label: '光源类型', value: '混合' },
      { label: '分辨率', value: '2K' },
      { label: '云台上下方向', value: '反向' },
      { label: '云台左右方向', value: '正向' },
      { label: '延时关灯开关', value: '开启' },
      { label: '语言', value: '中文' },
    ],
  },
  'fw_main_v2.0': {
    testItems: ['图像测试', 'WIFI信号', 'PTZ测试', 'PIR检测', '蓝牙测试', '复位测试'],
    otaTest: true, otaVersion: 'V2.1.0', otaFileName: 'main_fw_v2.1.0_ota.bin',
    config: [
      { label: '硬件类型', value: '类型B' },
      { label: '硬件板子新旧', value: '新板' },
      { label: '光源类型', value: '白光' },
      { label: '分辨率', value: '4K' },
      { label: '网口配置', value: '有线' },
      { label: '云台左右角度', value: '3600' },
      { label: '云台上下角度', value: '1800' },
      { label: '语言', value: '英文' },
    ],
  },
  'fw_wifi_v1.0': {
    testItems: ['WIFI信号', '蓝牙测试'],
    config: [
      { label: '网口配置', value: '无线' },
      { label: '语言', value: '中文' },
    ],
  },
  'fw_wifi_v1.2': {
    testItems: ['WIFI信号', '蓝牙测试', '复位测试'],
    config: [
      { label: '网口配置', value: '无线' },
      { label: '语言', value: '多语言' },
    ],
  },
  'fw_mcu_v1.0': {
    testItems: ['电池测试', '指示灯测试', '电源插口测试'],
    config: [
      { label: '硬件类型', value: '类型A' },
      { label: '语言', value: '中文' },
    ],
  },
  'fw_mcu_v1.1': {
    testItems: ['电池测试', '指示灯测试', '复位测试', '电源插口测试'],
    config: [
      { label: '硬件类型', value: '类型A' },
      { label: '硬件板子新旧', value: '旧板' },
      { label: '语言', value: '中文' },
    ],
  },
};

const GROUPS = [
  { value: 'default', label: '默认分组' },
  { value: 'groupA', label: 'A组' },
  { value: 'groupB', label: 'B组' },
];

const MOCK_BALANCE = 8600;

const CreateOrder = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const productType = Form.useWatch('productType', form) || 'machine';
  const [toolType, setToolType] = useState('platform');
  const [writeMode, setWriteMode] = useState('tool');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProdFw, setSelectedProdFw] = useState(null);
  const [fwConfigVisible, setFwConfigVisible] = useState(false);
  const [modelDrawerVisible, setModelDrawerVisible] = useState(false);

  const fwConfig = selectedProdFw ? MOCK_FW_CONFIG[selectedProdFw] : null;

  const handleSubmit = async (openGift) => {
    try {
      await form.validateFields();
      const orderName = form.getFieldValue('orderName') || '新工单';
      if (openGift) {
        // Mock: 创建工单后跳转赠送套餐，携带模拟工单信息
        message.success('工单创建成功，即将跳转赠送套餐');
        // 使用 sessionStorage 传递 mock 工单数据，让开发人员一眼分辨
        sessionStorage.setItem('giftMockOrder', JSON.stringify({
          orderId: 'WO-NEW-20260521-MOCK',
          orderName: orderName + '（Mock新工单）',
          productName: PRODUCTS.find(p => p.value === selectedProduct)?.label || '',
          productId: selectedProduct || '',
          licenseTotal: form.getFieldValue('authCount') || 500,
        }));
        navigate('/package/gift?openGift=1');
      } else {
        message.success('工单创建成功');
        navigate('/production/productionOrder');
      }
    } catch { /* validation failed */ }
  };

  const handleToolChange = (e) => {
    setToolType(e.target.value);
    form.resetFields(['product', 'tagTemplate', 'authRule', 'authCount', 'prodFw', 'writeMode', 'printPwd', 'group', 'thirdPartyFile']);
    setWriteMode('tool'); setSelectedProduct(null); setSelectedProdFw(null);
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        <Button icon={<ArrowLeftOutlined />} type="text" onClick={() => navigate('/production/productionOrder')} />
        <Title level={4} style={{ margin: 0 }}>创建工单</Title>
      </div>

      <Card bordered={false} style={{ maxWidth: 960 }}>
        <Form form={form} layout="vertical" initialValues={{ tool: 'platform', writeMode: 'tool', authRule: 'HMY', group: 'default' }}>

          {/* 基本信息 */}
          <Divider orientation="left" plain>基本信息</Divider>
          <Form.Item name="orderName" label="工单名称" rules={[{ required: true, message: '请输入工单名称' }]}>
            <Input placeholder="请输入工单名称" />
          </Form.Item>
          <Form.Item name="productType" label="设备类型" initialValue="machine" rules={[{ required: true }]}>
            <Radio.Group onChange={() => { form.setFieldsValue({ product: undefined }); setSelectedProduct(null); }}>
              <Radio value="plan">方案产品</Radio>
              <Radio value="machine">整机产品</Radio>
            </Radio.Group>
          </Form.Item>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item name="tool" label="生产工具" rules={[{ required: true }]}>
                <Radio.Group onChange={handleToolChange}>
                  <Radio value="platform">平台产测</Radio>
                  <Radio value="non-platform">非平台产测</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="product" label="生产产品" rules={[{ required: true, message: '请选择生产产品' }]}>
                <Select
                  placeholder={productType === 'plan' ? '请选择方案产品' : '请选择整机产品'}
                  showSearch
                  optionFilterProp="label"
                  options={productType === 'plan' ? PLAN_PRODUCTS : PRODUCTS}
                  onChange={(v) => setSelectedProduct(v)}
                />
              </Form.Item>
              {selectedProduct && <div style={{ marginTop: -20, marginBottom: 8 }}><Text type="secondary" style={{ fontSize: 12 }}>产品ID：{selectedProduct}</Text></div>}
            </Col>
          </Row>

          {/* 生产授权 */}
          <Divider orientation="left" plain>生产授权</Divider>
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">当前产品可生产授权余额：<Text strong>{MOCK_BALANCE.toLocaleString()}</Text></Text>
          </div>
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item name="authRule" label={<span>生成规则 <Tooltip title="License号生成规则：企业唯一标识码 + 自定义3位 + 年月日6位 + 随机数4位"><InfoCircleOutlined style={{ color: '#8c8c8c' }} /></Tooltip></span>} rules={[{ required: true, message: '请输入' }, { pattern: /^[A-Za-z0-9]{3}$/, message: '仅支持3位数字或字母' }]}>
                <Input maxLength={3} placeholder="3位数字或字母" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="authCount" label="添加授权（生产数量）" rules={[{ required: true, message: '请输入生产数量' }]} extra="单次最大添加5000个">
                <InputNumber min={MOCK_BALANCE === 0 ? 0 : 1} max={5000} style={{ width: '100%' }} placeholder="请输入数量" status={MOCK_BALANCE === 0 ? 'error' : undefined} />
              </Form.Item>
            </Col>
            {toolType === 'platform' && (
              <Col span={8}>
                <Form.Item name="thirdPartyFile" label={<span>三方授权 <Tooltip title="设备写号阶段，若需烧录额外信息请上传（与授权号License一同写入设备）"><InfoCircleOutlined style={{ color: '#8c8c8c' }} /></Tooltip></span>}>
                  <Upload maxCount={1} beforeUpload={() => false} accept=".zip,.xls,.xlsx">
                    <Button icon={<UploadOutlined />}>上传文件</Button>
                  </Upload>
                  <Text type="secondary" style={{ fontSize: 12 }}>支持 .zip、.xls、.xlsx</Text>
                </Form.Item>
              </Col>
            )}
          </Row>

          {/* 平台产测配置 */}
          {toolType === 'platform' && (
            <>
              <Divider orientation="left" plain>产测配置</Divider>

              {/* 生产固件：必选 + 固件配置同行 */}
              <Row gutter={24} align="top">
                <Col span={12}>
                    <Form.Item name="prodFw" label="生产固件" rules={[{ required: true, message: '请选择生产固件' }]}>
                      <Select showSearch optionFilterProp="label" placeholder="请选择生产固件" onChange={(v) => { setSelectedProdFw(v); const cfg = MOCK_FW_CONFIG[v]; form.setFieldsValue({ otaUpgrade: cfg?.otaTest ?? false }); }}>
                        {FIRMWARE_OPTIONS.map(fw => (
                          <Select.Option key={fw.value} value={fw.value} label={fw.label} disabled={!fw.finalized}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span>{fw.label}</span>
                              <Tag color={fw.finalized ? 'green' : 'default'} style={{ marginRight: 0 }}>{fw.finalized ? '已定版' : '未定版'}</Tag>
                            </div>
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                </Col>
                {fwConfig && (
                  <>
                    <Col span={6}>
                      <Form.Item label={<div>产品能力<br /><Text type="secondary" style={{ fontSize: 12, fontWeight: 'normal' }}>产品物模型</Text></div>}>
                        <Button type="link" style={{ padding: 0 }} onClick={() => setModelDrawerVisible(true)}>查看配置</Button>
                      </Form.Item>
                    </Col>
                    {fwConfig.config.length > 0 && (
                      <Col span={6}>
                        <Form.Item label={<div>固件配置<br /><Text type="secondary" style={{ fontSize: 12, fontWeight: 'normal' }}>固件TF卡配置</Text></div>}>
                          <Button type="link" style={{ padding: 0 }} onClick={() => setFwConfigVisible(true)}>查看配置</Button>
                        </Form.Item>
                      </Col>
                    )}
                  </>
                )}
              </Row>

              {/* 选择固件后关联显示生产测试项 */}
              {fwConfig && (
                <>
                  <Form.Item label="生产测试项">
                    <Space size={[4, 8]} wrap>{fwConfig.testItems.map(item => <Tag key={item}>{item}</Tag>)}</Space>
                  </Form.Item>
                  {fwConfig.otaTest !== undefined && (
                    <Form.Item name="otaUpgrade" label="固件升级" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  )}
                </>
              )}

              <Divider orientation="left" plain>其他配置</Divider>
              <Row gutter={24}>
                <Col span={8}>
                  <Form.Item name="group" label="产测分组（分配生产）">
                    <Select placeholder="请选择分组" options={GROUPS} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="writeMode" label={<span>写号方式 <Tooltip title="推荐产测工具烧录授权码，扫码枪需提前批量打标"><InfoCircleOutlined style={{ color: '#8c8c8c' }} /></Tooltip></span>}>
                    <Radio.Group onChange={(e) => setWriteMode(e.target.value)}>
                      <Radio value="tool">工具写号</Radio>
                      <Radio value="scanner">扫码枪</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                {writeMode === 'scanner' && (
                  <Col span={8}>
                    <Form.Item name="printPwd" label="打标密码" rules={[{ required: true, message: '请输入打标密码' }]} extra="防止生产人员滥打印标签">
                      <Input.Password placeholder="请输入打标密码" />
                    </Form.Item>
                  </Col>
                )}
              </Row>
            </>
          )}

          <Divider />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => navigate('/production/productionOrder')}>取消</Button>
            {toolType === 'platform' && (
              <Button icon={<GiftOutlined />} onClick={() => handleSubmit(true)} style={{ background: '#faad14', borderColor: '#faad14', color: '#fff' }}>保存并赠送套餐</Button>
            )}
            <Button type="primary" onClick={() => handleSubmit(false)}>保存工单</Button>
          </div>
        </Form>
      </Card>

      {/* 固件配置 - 只读文本弹窗 */}
      <Modal title="固件配置" open={fwConfigVisible} onCancel={() => setFwConfigVisible(false)} destroyOnClose width={600}
        footer={<Button onClick={() => setFwConfigVisible(false)}>关闭</Button>}>
        {fwConfig && (
          <div style={{ marginTop: 8 }}>
            {fwConfig.config.map((item, i) => (
              <div key={i} style={{ display: 'flex', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                <Text type="secondary" style={{ width: 180, flexShrink: 0 }}>{item.label}</Text>
                <Text>{item.value}</Text>
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* 产品能力 - 物模型配置 Drawer */}
      <Drawer title="物模型配置" width={720} open={modelDrawerVisible} onClose={() => setModelDrawerVisible(false)}>
        <div style={{ padding: '0 8px' }}>
          {[
            { title: '设备能力', items: [
              { label: '设备类型', value: 'IPC摄像机' },
              { label: '联网方式', value: 'Wi-Fi' },
              { label: '供电方式', value: '直流供电' },
            ]},
            { title: '镜头', items: [
              { label: '镜头数量', value: '1' },
              { label: '镜头类型', value: '普通镜头' },
              { label: '最大焦距', value: '4.0mm' },
              { label: '最小焦距', value: '4.0mm' },
            ]},
            { title: '麦克风', items: [
              { label: '麦克风', value: '支持' },
              { label: '拾音方式', value: '内置麦克风' },
            ]},
            { title: '云台', items: [
              { label: '云台', value: '支持' },
              { label: '水平转动角度', value: '355°' },
              { label: '垂直转动角度', value: '90°' },
            ]},
            { title: '存储', items: [
              { label: 'TF卡', value: '支持' },
              { label: '最大容量', value: '128GB' },
            ]},
          ].map((section, idx) => (
            <Card key={idx} size="small" title={<Text strong>{section.title}</Text>} style={{ marginBottom: 16, borderRadius: 8 }}>
              {section.items.map((item, i) => (
                <div key={i} style={{ display: 'flex', padding: '8px 0', borderBottom: i < section.items.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                  <Text type="secondary" style={{ width: 140, flexShrink: 0 }}>{item.label}</Text>
                  <Text>{item.value}</Text>
                </div>
              ))}
            </Card>
          ))}
        </div>
      </Drawer>

    </div>
  );
};

export default CreateOrder;
